import styled, { css } from 'styled-components'
import { StyledDomElement } from '../../App'
import {
  BarryUIColorKey,
  customPadding,
  paddingRules,
  BarryUISpacingKey,
  withIndexGutters,
  BarrySurfaceWithStateProps,
  barrySurface,
  indexGutterMargin,
  withIndexGuttersPadding,
} from '../../theme'

export const Box = styled.div<
  {
    direction?: string | null
    dc?: boolean
    dr?: boolean
    dcr?: boolean
    drr?: boolean
    c?: boolean
    ac?: boolean
    afs?: boolean
    afe?: boolean
    jc?: boolean
    jfs?: boolean
    jfe?: boolean
    jsb?: boolean
    jsa?: boolean
    jse?: boolean
    asb?: boolean
    asa?: boolean
    ase?: boolean
    fu?: boolean
    fw?: boolean
    mc?: boolean
    z?: number
    justify?: string | null
    align?: string | null
    alignSelf?: string
    pss?: boolean
    flex?: number | string | 'auto' | 'none' | 'unset' | null
    marginTop?: number | string | null
    marginBottom?: number | null
    marginRight?: number | null
    marginLeft?: number | null
    padding?: string
    paddingMobile?: string
    wrap?: string | null
    zIndex?: number
    relative?: boolean
    pr?: boolean
    center?: boolean
    centerMobile?: boolean
    gap?: number
    g?: number | string
    mGap?: number
    columnMobile?: boolean
    rowMobile?: boolean
    mobileAlign?: string
    threshold?: number
    autoWidth?: boolean
    aw?: boolean
    w?: string
    mw?: string
    minW?: string
    mh?: string
    minH?: string
    bgc?: BarryUIColorKey
    br?: number
    sp?: boolean // standard padding
    cp?: boolean // cursor pointer
    ig?: boolean // indexGutters
    igmv?: boolean // indexGutters margin vertical
    igmh?: boolean // indexGutters margin horizontal
    mhauto?: boolean // margin horizontal auto
    igpv?: boolean // indexGutters padding vertical
    igph?: boolean // indexGutters padding horizontal
    pad?: BarryUISpacingKey // pulls different padding values from theme
    padL?: BarryUISpacingKey // pulls different padding values from theme
    padR?: BarryUISpacingKey // pulls different padding values from theme
    padT?: BarryUISpacingKey // pulls different padding values from theme
    padB?: BarryUISpacingKey // pulls different padding values from theme
    padV?: BarryUISpacingKey // pulls different padding values from theme
    padH?: BarryUISpacingKey // pulls different padding values from theme
  } & StyledDomElement &
  BarrySurfaceWithStateProps
>`
  display: flex;
  ${({ gap, g }) =>
    gap || g ? `gap:${gap || g}${typeof g === 'string' ? '' : 'px'};` : ''}
  justify-content: ${({ justify }) => justify ?? 'flex-start'};
  align-items: ${({ align }) => align ?? 'flex-start'};
  ${({ alignSelf }) =>
    alignSelf &&
    css`
      align-self: ${alignSelf};
    `};
  ${({ pss }) =>
    pss &&
    css`
      place-self: stretch;
    `};
  ${({ direction }) =>
    direction &&
    css`
      flex-direction: ${direction};
    `};
  ${({ dc }) =>
    dc &&
    css`
      flex-direction: column;
    `}
  ${({ dr }) =>
    dr &&
    css`
      flex-direction: row;
    `}
  ${({ dcr }) =>
    dcr &&
    css`
      flex-direction: column-reverse;
    `}
  ${({ drr }) =>
    drr &&
    css`
      flex-direction: row-reverse;
    `}
  ${({ ac }) =>
    ac &&
    css`
      align-items: center;
    `}
  ${({ jc }) =>
    jc &&
    css`
      justify-content: center;
    `}
  ${({ jfe }) =>
    jfe &&
    css`
      justify-content: flex-end;
    `}
  ${({ jfs }) =>
    jfs &&
    css`
      justify-content: flex-start;
    `}
  ${({ jsb }) =>
    jsb &&
    css`
      justify-content: space-between;
    `}
  ${({ jsa }) =>
    jsa &&
    css`
      justify-content: space-around;
    `}
  ${({ jse }) =>
    jse &&
    css`
      justify-content: space-evenly;
    `}
  ${({ afs }) =>
    afs &&
    css`
      align-items: flex-start;
    `}
  ${({ afe }) =>
    afe &&
    css`
      align-items: flex-end;
    `}
  ${({ asb }) =>
    asb &&
    css`
      align-items: space-between;
    `}
  ${({ asa }) =>
    asa &&
    css`
      align-items: space-around;
    `}
  ${({ ase }) =>
    ase &&
    css`
      align-items: space-evenly;
    `}
  flex: ${({ flex }) => flex ?? 1};
  ${({ wrap, fw }) => (wrap || fw ? `flex-wrap:${fw ? 'wrap' : wrap};` : '')};
  ${({ zIndex, z }) => (zIndex || z ? `z-index:${zIndex ?? z};` : '')};
  ${({ marginTop }) =>
    marginTop
      ? `margin-top:${marginTop}${typeof marginTop === 'string' ? '' : 'px'};`
      : ''};
  ${({ marginRight }) => (marginRight ? `margin-right:${marginRight}px;` : '')};
  ${({ marginBottom }) =>
    marginBottom ? `margin-bottom:${marginBottom}px;` : ''};
  width: 100%;
  ${({ marginLeft }) => (marginLeft ? `margin-left:${marginLeft}px;` : '')};
  ${({ mhauto }) => mhauto && `margin-left: auto; margin-right: auto;`}
  ${({ padding }) => (padding ? `padding:${padding};` : '')};
  ${({ paddingMobile, threshold }) =>
    paddingMobile &&
    css`
      @media (max-width: ${threshold ?? 1000}px) {
        padding: ${paddingMobile};
      }
    `}
  ${({ relative, pr }) =>
    (relative || pr) &&
    css`
      position: relative;
    `}
  ${({ center, c }) =>
    (center || c) &&
    css`
      justify-content: center;
      align-items: center;
    `}
  ${({ centerMobile }) =>
    centerMobile &&
    css`
      @media (max-width: 1000px) {
        justify-content: center;
        align-items: center;
      }
    `}
  ${({ columnMobile, threshold }) =>
    columnMobile &&
    css`
      @media (max-width: ${threshold ?? 1000}px) {
        flex-direction: column;
      }
    `}

  ${({ rowMobile, threshold }) =>
    rowMobile &&
    css`
      @media (max-width: ${threshold ?? 1000}px) {
        flex-direction: row;
      }
    `}

  ${({ mobileAlign }) =>
    mobileAlign &&
    css`
      @media (max-width: 1000px) {
        align-items: ${mobileAlign};
      }
    `}

    ${({ mGap }) =>
    mGap &&
    css`
      @media (max-width: 1000px) {
        gap: ${mGap}px;
      }
    `}

    ${({ fu }) =>
    fu &&
    css`
      flex: unset;
    `}

    ${({ autoWidth, aw }) =>
    (autoWidth || aw) &&
    css`
      width: auto;
    `}


    ${({ w }) =>
    w &&
    css`
      width: ${w};
    `}

    ${({ mw }) =>
    mw &&
    css`
      max-width: ${mw};
    `}
    ${({ mh }) =>
    mh &&
    css`
      max-height: ${mh};
    `}

    ${({ minW }) =>
    minW &&
    css`
      min-width: ${minW};
    `}

    ${({ minH }) =>
    minH &&
    css`
      min-height: ${minH};
    `}

    @media screen and (max-width: 768px) {
    ${({ mc }) =>
    mc &&
    css`
        flex-direction: column;
      `}
  }

  ${({ br }) =>
    br &&
    css`
      border-radius: ${br}px;
    `}

  ${({ bgc, theme }) => {
    if (!bgc) return ''
    const [parentKey, childKey] = bgc.split('-')
    const color = theme.semantics.zap?.[parentKey]?.[childKey]
    if (!color) return ''
    return `
        background-color: ${color};
        `
  }}
${({ cp }) =>
    cp &&
    css`
      cursor: pointer;
    `}
${({ sp }) => sp && paddingRules}
${customPadding}
${({ ig }) => ig && withIndexGutters}
${({ igmv, igmh }) => (igmv || igmh) && indexGutterMargin}
${({ igpv, igph }) => (igpv || igph) && withIndexGuttersPadding}
${barrySurface}
`
